import { FarmerContext } from './types';

export class ConversationContext {
  private context: FarmerContext;

  constructor() {
    this.context = this.loadContext();
  }

  private loadContext(): FarmerContext {
    const stored = localStorage.getItem('agriagent_context');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects
      parsed.recentActivities = parsed.recentActivities?.map((a: any) => ({
        ...a,
        date: new Date(a.date),
      })) || [];
      parsed.currentCrops = parsed.currentCrops?.map((c: any) => ({
        ...c,
        plantedDate: new Date(c.plantedDate),
      })) || [];
      parsed.pendingTasks = parsed.pendingTasks?.map((t: any) => ({
        ...t,
        dueDate: new Date(t.dueDate),
      })) || [];
      return parsed;
    }

    return {
      recentActivities: [],
      currentCrops: [],
      weather: { temp: 25, conditions: 'Sunny', forecast: 'Clear skies' },
      pendingTasks: [],
      recentQuestions: [],
    };
  }

  private saveContext(): void {
    localStorage.setItem('agriagent_context', JSON.stringify(this.context));
  }

  getContext(): FarmerContext {
    return { ...this.context };
  }

  updateWeather(weather: { temp: number; conditions: string; forecast: string }): void {
    this.context.weather = weather;
    this.saveContext();
  }

  addActivity(type: string, crop?: string): void {
    this.context.recentActivities.unshift({
      type,
      date: new Date(),
      crop,
    });
    // Keep only last 20 activities
    this.context.recentActivities = this.context.recentActivities.slice(0, 20);
    this.saveContext();
  }

  addCrop(name: string, stage: string): void {
    this.context.currentCrops.push({
      name,
      stage,
      plantedDate: new Date(),
    });
    this.saveContext();
  }

  updateCropStage(cropName: string, stage: string): void {
    const crop = this.context.currentCrops.find((c) => c.name === cropName);
    if (crop) {
      crop.stage = stage;
      this.saveContext();
    }
  }

  addTask(task: string, dueDate: Date): void {
    this.context.pendingTasks.push({ task, dueDate });
    this.saveContext();
  }

  removeTask(task: string): void {
    this.context.pendingTasks = this.context.pendingTasks.filter((t) => t.task !== task);
    this.saveContext();
  }

  addQuestion(question: string): void {
    this.context.recentQuestions.unshift(question);
    // Keep only last 10 questions
    this.context.recentQuestions = this.context.recentQuestions.slice(0, 10);
    this.saveContext();
  }

  getSummary(): string {
    const { recentActivities, currentCrops, pendingTasks } = this.context;
    
    let summary = '';
    
    if (currentCrops.length > 0) {
      summary += `Current crops: ${currentCrops.map((c) => `${c.name} (${c.stage})`).join(', ')}. `;
    }
    
    if (recentActivities.length > 0) {
      const recent = recentActivities.slice(0, 3);
      summary += `Recent activities: ${recent.map((a) => a.type).join(', ')}. `;
    }
    
    if (pendingTasks.length > 0) {
      summary += `${pendingTasks.length} pending task(s). `;
    }
    
    return summary || 'No recent farming activity recorded.';
  }

  clear(): void {
    this.context = {
      recentActivities: [],
      currentCrops: [],
      weather: { temp: 25, conditions: 'Sunny', forecast: 'Clear skies' },
      pendingTasks: [],
      recentQuestions: [],
    };
    this.saveContext();
  }
}

export const conversationContext = new ConversationContext();
